import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import type {
  WhatsAppContactInsights,
  WhatsAppThreadContact,
} from '../../models/MasterAdmin/whatsappHub';
import { fetchInboxContactInsights, updateInboxContact } from '../../services/MasterAdmin/whatsappCampaignService';
import { formatWaTime, initials } from './whatsappUi';

type SidebarTab = 'details' | 'analysis';

type WhatsAppInboxContactSidebarProps = {
  countryCode: string;
  phoneNumber: string;
  displayPhone: string;
  contact: WhatsAppThreadContact;
  notes: string;
  onNotesChange: (value: string) => void;
  onNotesSaved?: () => void;
};

function statTile(label: string, value: string | number, tone?: 'green' | 'amber' | 'red') {
  const toneClass =
    tone === 'green'
      ? 'text-[#128C7E]'
      : tone === 'amber'
        ? 'text-amber-600'
        : tone === 'red'
          ? 'text-red-600'
          : 'text-gray-900 dark:text-white';
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-2.5 dark:border-gray-700 dark:bg-gray-900">
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

const WhatsAppInboxContactSidebar: React.FC<WhatsAppInboxContactSidebarProps> = ({
  countryCode,
  phoneNumber,
  displayPhone,
  contact,
  notes,
  onNotesChange,
  onNotesSaved,
}) => {
  const [tab, setTab] = useState<SidebarTab>('details');
  const [savingNotes, setSavingNotes] = useState(false);
  const [insights, setInsights] = useState<WhatsAppContactInsights | null>(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  useEffect(() => {
    setTab('details');
  }, [countryCode, phoneNumber]);

  useEffect(() => {
    if (tab !== 'analysis') return;
    let cancelled = false;
    setLoadingInsights(true);
    fetchInboxContactInsights(countryCode, phoneNumber)
      .then((res) => {
        if (!cancelled) setInsights(res.data);
      })
      .catch(() => {
        if (!cancelled) toast.error('Failed to load contact analysis');
      })
      .finally(() => {
        if (!cancelled) setLoadingInsights(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, countryCode, phoneNumber]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await updateInboxContact(countryCode, phoneNumber, { notes });
      toast.success('Notes saved');
      onNotesSaved?.();
    } catch {
      toast.error('Failed to save notes');
    } finally {
      setSavingNotes(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-gray-200 bg-[#f0f2f5] px-3 py-2 dark:border-gray-700 dark:bg-[#202c33]">
        <div className="grid grid-cols-2 gap-1 rounded-lg bg-white p-1 dark:bg-gray-900">
          {(['details', 'analysis'] as SidebarTab[]).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`rounded-md px-2 py-1.5 text-xs font-medium capitalize ${
                tab === key
                  ? 'bg-[#25D366] text-white'
                  : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800'
              }`}
            >
              {key === 'details' ? 'Contact details' : 'Analysis'}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 text-sm">
        {tab === 'details' ? (
          <div className="space-y-4">
            <div className="flex flex-col items-center gap-2 py-2">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#dfe5e7] text-lg font-semibold dark:bg-[#6b7b85] dark:text-white">
                {initials(contact.displayName)}
              </div>
              <p className="text-center font-semibold text-gray-900 dark:text-white">{contact.displayName}</p>
              <p className="text-xs text-gray-500 dark:text-[#8696a0]">{displayPhone}</p>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Type</p>
              <p className="capitalize text-gray-700 dark:text-gray-200">{contact.recipientType}</p>
            </div>
            {contact.email && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Email</p>
                <p className="text-gray-700 dark:text-gray-200">{contact.email}</p>
              </div>
            )}
            {contact.city && (
              <div>
                <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">City</p>
                <p className="text-gray-700 dark:text-gray-200">{contact.city}</p>
              </div>
            )}
            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">WhatsApp opt-in</p>
              <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-800">Yes</span>
            </div>

            <div>
              <p className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-400">Notes</p>
              <textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-200 p-2 text-sm dark:border-gray-600 dark:bg-gray-800"
                placeholder="Internal notes about this contact..."
              />
              <button
                type="button"
                disabled={savingNotes}
                onClick={handleSaveNotes}
                className="mt-2 rounded-lg bg-gray-900 px-3 py-1.5 text-xs text-white disabled:opacity-50 dark:bg-gray-700"
              >
                {savingNotes ? 'Saving...' : 'Save notes'}
              </button>
            </div>
          </div>
        ) : loadingInsights ? (
          <p className="text-sm text-gray-500">Loading analysis...</p>
        ) : insights ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {statTile('Total msgs', insights.stats.totalMessages)}
              {statTile('Sent', insights.stats.outbound)}
              {statTile('Replies', insights.stats.inbound, 'green')}
              {statTile('Unread replies', insights.stats.unreadInbound, 'amber')}
              {statTile('Delivered', insights.stats.delivered)}
              {statTile('Read', insights.stats.read, 'green')}
              {statTile('Failed', insights.stats.failed, insights.stats.failed ? 'red' : undefined)}
            </div>

            <div className="rounded-lg border border-gray-200 p-3 dark:border-gray-700">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Activity</p>
              <dl className="mt-2 space-y-1 text-xs text-gray-600 dark:text-gray-300">
                <div className="flex justify-between gap-2">
                  <dt>First message</dt>
                  <dd>{insights.stats.firstContactAt ? formatWaTime(insights.stats.firstContactAt) : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>Last message</dt>
                  <dd>{insights.stats.lastMessageAt ? formatWaTime(insights.stats.lastMessageAt) : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>Last reply</dt>
                  <dd>{insights.stats.lastInboundAt ? formatWaTime(insights.stats.lastInboundAt) : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>Last sent</dt>
                  <dd>{insights.stats.lastOutboundAt ? formatWaTime(insights.stats.lastOutboundAt) : '—'}</dd>
                </div>
              </dl>
            </div>

            {insights.templateUsage.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Templates used</p>
                <ul className="space-y-1 text-xs">
                  {insights.templateUsage.map((t) => (
                    <li
                      key={t.name}
                      className="flex items-center justify-between rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-gray-800/80"
                    >
                      <span className="truncate font-medium">{t.name}</span>
                      <span className="text-gray-500">{t.count}×</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {insights.recentInbound.length > 0 && (
              <div>
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Recent replies</p>
                <ul className="space-y-2">
                  {insights.recentInbound.map((m) => (
                    <li
                      key={m.id}
                      className="rounded-lg border border-[#25D366]/20 bg-[#25D366]/5 p-2 text-xs dark:border-[#25D366]/30"
                    >
                      <p className="whitespace-pre-wrap break-words text-gray-800 dark:text-gray-100">
                        {m.renderedBody || m.body}
                      </p>
                      <p className="mt-1 text-[10px] text-gray-500">{formatWaTime(m.createdAt)}</p>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Timeline</p>
              <ul className="space-y-2">
                {insights.timeline.map((item) => (
                  <li key={`${item.kind}-${item.id}`} className="rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                    <div className="flex items-start justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                        {item.kind === 'inbound_message'
                          ? 'Reply received'
                          : item.kind === 'outbound_message'
                            ? 'Message sent'
                            : 'Delivery update'}
                      </span>
                      <span className="shrink-0 text-[10px] text-gray-500">{formatWaTime(item.at)}</span>
                    </div>
                    {item.body && (
                      <p className="mt-1 whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-200">
                        {item.body}
                      </p>
                    )}
                    {item.templateName && (
                      <p className="mt-1 text-[10px] text-gray-500">Template: {item.templateName}</p>
                    )}
                    {item.status && (
                      <p className="mt-1 text-[10px] capitalize text-gray-500">Status: {item.status}</p>
                    )}
                    {item.errorMessage && (
                      <p className="mt-1 text-[10px] text-red-600">{item.errorMessage}</p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">No analysis available yet.</p>
        )}
      </div>
    </div>
  );
};

export default WhatsAppInboxContactSidebar;
